import ValidationItem from './validation-item';

export default class ValidationGroup {
	id: string;
	label?: string;
	validationItems: (ValidationGroup | ValidationItem)[] = [];

	constructor(init: Partial<ValidationGroup>) {
		if (!init?.id) {
			throw new Error('An id is required to initialize a ValidationGroup');
		}
		this.id = init.id;
		this.label = init.label;
		if (init.validationItems && init.validationItems.length > 0) {
			for (const element of init.validationItems) {
				if (Object.hasOwn(element, 'validationItems')) {
					this.validationItems.push(new ValidationGroup(element));
				} else {
					this.validationItems.push(new ValidationItem(element));
				}
			}
		} else {
			this.validationItems = [];
		}
	}

	isValid() {
		for (const validationItem of this.validationItems) {
			if (!validationItem.isValid()) {
				return false;
			}
		}
		return true;
	}

	public findValidationObjectByID(
		validationGroup: ValidationGroup,
		objectID: string
	): ValidationGroup | ValidationItem | undefined {
		if (validationGroup.id === objectID) {
			return validationGroup;
		}

		const directMatch = validationGroup.validationItems.find(x => x.id === objectID);
		if (directMatch) {
			return directMatch;
		}

		for (const validationItem of validationGroup.validationItems) {
			if (validationItem instanceof ValidationGroup) {
				const result = this.findValidationObjectByID(validationItem, objectID);
				if (result) {
					return result;
				}
			}
		}

		return undefined;
	}

	public getTaskValidationErrors(taskIds: string[]): {
		title: string;
		taskValidations: { id: string; errorMessage: string }[];
	} {
		const res = `${this.label ?? this.id}`;
		const taskValidations: { id: string; errorMessage: string }[] = [];
		for (const id of taskIds) {
			const validation = this.findValidationObjectByID(this, id);
			if (!validation) continue;
			if (validation.isValid()) continue;
			if (validation instanceof ValidationGroup) {
				taskValidations.push({ id, errorMessage: validation.getErrorMessages().join('\n') });
			}
		}
		return { title: `${res} has validation errors.`, taskValidations };
	}

	private setItemError(validationGroup: ValidationGroup, validationItem: ValidationItem) {
		const existingItem = validationGroup.validationItems.find(x => x.id === validationItem.id);
		if (existingItem && existingItem instanceof ValidationItem) {
			existingItem.errorMessage = validationItem.errorMessage;
			return;
		}
		validationGroup.validationItems.push(validationItem);
	}

	setError(validationItem: ValidationItem, groupID?: string) {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		let group: ValidationGroup | ValidationItem | undefined = this;
		if (groupID) {
			group = this.findValidationObjectByID(this, groupID);
			if (!group || !(group instanceof ValidationGroup)) {
				throw new Error(`Could not find a validation group with the ID: ${groupID}`);
			}
		}
		this.setItemError(group, validationItem);
	}

	deleteValidation(id: string) {
		if (id === this.id) return true;
		for (let index = 0; index < this.validationItems.length; index++) {
			const group = this.validationItems[index];
			if (group.deleteValidation(id)) {
				this.validationItems.splice(index, 1);
				break;
			}
		}
		return false;
	}

	private getErrorMessages(messages: string[] = []) {
		for (const validationItem of this.validationItems) {
			const label = validationItem.label ?? validationItem.id;
			if (validationItem instanceof ValidationGroup) {
				const tempMsgs = validationItem.getErrorMessages();
				if (tempMsgs.length > 0) {
					messages.push(`${label} - ${tempMsgs.map(msg => ` ${msg}`).join('')}`);
				}
			} else if (!validationItem.isValid()) {
				messages.push(`\n\t${label} - ${validationItem.errorMessage}`);
			}
		}
		return messages;
	}

	hasValidationItems(): boolean {
		for (const validationItem of this.validationItems) {
			if (validationItem instanceof ValidationItem) return true;
			const res = validationItem.hasValidationItems();
			if (res) return res;
		}
		return false;
	}

	toString() {
		const res = `${this.label ?? this.id}`;
		const errMessages = this.getErrorMessages();
		if (errMessages.length === 0) {
			return `${res} is valid`;
		}
		return `${res} has validation errors:\n\n* ${errMessages.join(`\n*`)}`;
	}
}
